<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <h2>{{ $t('auth.forgotPassword') }}</h2>
        <p>{{ $t('auth.forgotPasswordSubtitle') }}</p>
      </div>
      
      <el-form
        ref="forgotForm"
        :model="forgotForm"
        :rules="forgotFormRules"
        label-width="100px"
        class="auth-form"
      >
        <el-form-item :label="$t('auth.username')" prop="phone">
          <el-input
            v-model="forgotForm.phone"
            :placeholder="$t('auth.enterPhone')"
            prefix-icon="el-icon-phone"
            clearable
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('auth.password')" prop="password">
          <el-input
            v-model="forgotForm.password"
            type="password"
            :placeholder="$t('auth.enterNewPassword')"
            prefix-icon="el-icon-lock"
            show-password
          ></el-input>
        </el-form-item>
        
        <el-form-item :label="$t('auth.captcha')" prop="captcha">
          <el-row :gutter="10">
            <el-col :span="14">
              <el-input
                v-model="forgotForm.captcha"
                :placeholder="$t('auth.enterCaptcha')"
                prefix-icon="el-icon-picture"
                clearable
              ></el-input>
            </el-col>
            <el-col :span="10">
              <div class="captcha-container">
                <img
                  :src="captchaUrl"
                  @click="getCaptcha"
                  :alt="$t('auth.refreshCaptcha')"
                  class="captcha-img"
                />
              </div>
            </el-col>
          </el-row>
        </el-form-item>

        <el-form-item :label="$t('auth.smsCaptcha')" prop="code">
          <el-row :gutter="10">
            <el-col :span="14">
              <el-input
                v-model="forgotForm.code"
                :placeholder="$t('auth.enterSmsCaptcha')"
                prefix-icon="el-icon-message"
                clearable
              ></el-input>
            </el-col>
            <el-col :span="10">
              <el-button 
                :disabled="smsLoading || smsCountDown > 0" 
                @click="handleSendSms"
                class="sms-btn"
              >
                {{ smsCountDown > 0 ? `${smsCountDown}s` : $t('auth.sendSms') }}
              </el-button>
            </el-col>
          </el-row>
        </el-form-item>
        
        <el-form-item class="auth-actions">
          <el-button type="primary" @click="handleForgotPassword" :loading="loading">
            {{ $t('auth.submit') }}
          </el-button>
          <el-button @click="handleReset">
            {{ $t('auth.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
      
      <div class="auth-footer">
        <p><a href="/login">{{ $t('auth.backToLogin') }}</a></p>
        <p><a href="/register">{{ $t('auth.register') }}</a></p>
      </div>
    </div>
  </div>
</template>

<script>
import { retrievePassword, getCaptcha, sendSmsVerification } from '@/apis/auth/auth.js'
import { getUUID } from '@/utils'
import { Sm2Crypto } from '@/utils/sm2'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      forgotForm: {
        phone: '',
        password: '',
        captcha: '',
        captchaId: '',
        code: ''
      },
      forgotFormRules: {
        phone: [
          { required: true, message: this.$t('auth.phoneRequired'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('auth.phoneInvalid'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('auth.passwordRequired'), trigger: 'blur' },
          { min: 6, max: 20, message: this.$t('auth.passwordLength'), trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: this.$t('auth.captchaRequired'), trigger: 'blur' },
          { min: 5, max: 5, message: '验证码长度必须为5位', trigger: 'blur' }
        ],
        code: [
          { required: true, message: this.$t('auth.smsCaptchaRequired'), trigger: 'blur' }
        ]
      },
      loading: false,
      smsLoading: false,
      smsCountDown: 0,
      captchaUrl: ''
    }
  },
  mounted() {
    // 页面加载时获取验证码
    this.getCaptchaImage()
  },
  methods: {
    // 获取验证码图片
    async getCaptchaImage() {
      try {
        // 生成新的UUID作为验证码ID
        this.forgotForm.captchaId = getUUID()
        
        const response = await getCaptcha(this.forgotForm.captchaId)
        
        // 释放之前的URL对象以防内存泄漏
        if (this.captchaUrl && this.captchaUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.captchaUrl)
        }
        
        this.captchaUrl = URL.createObjectURL(response)
        
        console.log('验证码获取成功, captchaId:', this.forgotForm.captchaId)
      } catch (error) {
        this.$message.error(this.$t('auth.getCaptchaError'))
        console.error('获取验证码失败:', error)
      }
    },
    
    // 刷新验证码
    getCaptcha() {
      this.getCaptchaImage()
      this.forgotForm.captcha = ''
    },

    // 发送短信验证码
    async handleSendSms() {
      // 验证手机号和图形验证码
      if (!/^1[3-9]\d{9}$/.test(this.forgotForm.phone)) {
        this.$message.error(this.$t('auth.phoneInvalid'))
        return
      }
      if (!this.forgotForm.captcha || this.forgotForm.captcha.length !== 5) {
        this.$message.error(this.$t('auth.captchaRequired'))
        return
      }

      this.smsLoading = true
      try {
        await sendSmsVerification({
          phone: this.forgotForm.phone,
          captcha: this.forgotForm.captcha,
          captchaId: this.forgotForm.captchaId
        })
        this.$message.success(this.$t('auth.smsSentSuccess'))
        this.smsCountDown = 60
        const timer = setInterval(() => {
          this.smsCountDown--
          if (this.smsCountDown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      } catch (error) {
        console.error('发送短信失败:', error)
        this.getCaptcha()
      } finally {
        this.smsLoading = false
      }
    },
    
    // 提交密码找回请求
    handleForgotPassword() {
      this.$refs.forgotForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          
          try {
            // 对验证码+密码组合进行SM2加密
            const encryptedPassword = await Sm2Crypto.encryptPasswordWithCaptcha(
              this.forgotForm.password, 
              this.forgotForm.captcha
            );

            await retrievePassword({
              phone: this.forgotForm.phone,
              password: encryptedPassword,
              captchaId: this.forgotForm.captchaId,
              code: this.forgotForm.code
            })
            
            this.$message.success(this.$t('auth.resetPasswordSuccess'))
            // 跳转到登录页面
            this.$router.push('/login')
          } catch (error) {
            console.error('密码找回请求失败:', error)
            // 请求失败后刷新验证码
            this.getCaptcha()
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    // 重置表单
    handleReset() {
      this.$refs.forgotForm.resetFields()
      this.getCaptcha()
    }
  }
}
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
}

.auth-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  max-width: 480px;
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.auth-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 24px;
}

.auth-header p {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.auth-form {
  margin-bottom: 20px;
}

.auth-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.auth-actions .el-button {
  width: 120px;
}

.auth-footer {
  text-align: center;
  color: #606266;
  font-size: 14px;
}

.auth-footer a {
  color: #409EFF;
  text-decoration: none;
  margin: 0 5px;
}

.auth-footer a:hover {
  text-decoration: underline;
}

.captcha-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.captcha-img {
  height: 36px;
  border-radius: 4px;
  cursor: pointer;
  display: block;
}

.sms-btn {
  width: 100%;
}
</style>